import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Rocket, Megaphone, AlertTriangle, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

const TYPE_META = {
  release_notes: { label: 'Release Notes', icon: Rocket, color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  announcement: { label: 'Announcement', icon: Megaphone, color: 'bg-[#ffd108]/20 text-[#ffd108] border-[#ffd108]/30' },
  maintenance: { label: 'Maintenance', icon: AlertTriangle, color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
};

export default function ReleaseNotes() {
  const { api } = useAuth();
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get('/announcements/release-notes');
        setItems(res.data || []);
      } catch (err) {
        console.error('Failed to fetch release notes:', err);
      } finally {
        setLoading(false);
      }
    };
    if (api) fetch();
  }, [api]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className={`text-2xl font-bold ${isLight ? 'text-gray-900' : 'text-white'} flex items-center gap-2`}>
          <Rocket className="h-6 w-6 text-[#ffd108]" />
          Release Notes & Announcements
        </h1>
        <p className={isLight ? 'text-gray-500' : 'text-slate-400'}>
          Stay up to date with the latest changes and improvements
        </p>
      </div>

      {items.length === 0 ? (
        <Card className={isLight ? 'bg-white border-gray-200' : 'bg-zinc-900 border-zinc-800'}>
          <CardContent className="py-16 text-center">
            <Megaphone className={`h-12 w-12 mx-auto mb-4 ${isLight ? 'text-gray-300' : 'text-zinc-600'}`} />
            <p className={isLight ? 'text-gray-500' : 'text-zinc-400'}>No announcements yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {items.map((item) => {
            const meta = TYPE_META[item.type] || TYPE_META.announcement;
            const Icon = meta.icon;
            return (
              <Card key={item.id} className={isLight ? 'bg-white border-gray-200 shadow-sm' : 'bg-zinc-900 border-zinc-800'} data-testid={`release-note-${item.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Badge className={meta.color}>
                      <Icon className="h-3 w-3 mr-1" />
                      {meta.label}
                    </Badge>
                    <span className={`text-sm ${isLight ? 'text-gray-500' : 'text-zinc-500'}`}>
                      {item.created_at && format(new Date(item.created_at), 'MMMM d, yyyy')}
                    </span>
                  </div>
                  <h2 className={`text-lg font-bold mb-3 ${isLight ? 'text-gray-900' : 'text-white'}`}>
                    {item.title}
                  </h2>
                  <div
                    className={`prose prose-sm max-w-none ${isLight ? '' : 'prose-invert'}`}
                    dangerouslySetInnerHTML={{ __html: item.body }}
                  />
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
